import React from 'react';
import { TrendingDown, Wallet, Target, Coins, Hourglass, Info, Trophy } from 'lucide-react';
import { Lead } from '../types';
import { KpiGrid } from './KpiGrid';
import { eur } from '../lib/utils';
interface MetricsViewProps {
  leads: Lead[];
}
export function MetricsView({
  leads
}: MetricsViewProps) {
  const total = leads.length;
  const reached = leads.filter(l => ['qual', 'cita', 'pres', 'win'].includes(l.stage)).length;
  const cita = leads.filter(l => ['cita', 'pres', 'win'].includes(l.stage)).length;
  const pres = leads.filter(l => ['pres', 'win'].includes(l.stage)).length;
  const win = leads.filter(l => l.stage === 'win').length;
  const pc = (a: number, b: number) => b > 0 ? Math.round(a / b * 100) : 0;
  const kpis = [{
    label: 'Total leads',
    value: total,
    subtitle: 'en el sistema'
  }, {
    label: 'Tasa de cierre',
    value: `${pc(win, total)}%`,
    subtitle: 'lead → obra',
    accent: 'wood' as const
  }, {
    label: 'Cierre de citas',
    value: `${pc(win, cita)}%`,
    subtitle: 'cita → obra',
    accent: 'green' as const
  }, {
    label: 'Obras ganadas',
    value: win,
    subtitle: 'cerradas'
  }];
  const steps = [{
    l: 'Leads totales',
    v: total
  }, {
    l: 'Contactados / cualificados',
    v: reached
  }, {
    l: 'Cita agendada',
    v: cita
  }, {
    l: 'Presupuesto dado',
    v: pres
  }, {
    l: 'Obra ganada',
    v: win
  }];
  const wonValue = leads.filter(l => l.stage === 'win').reduce((sum, l) => sum + (l.value || 0), 0);
  const avgTicket = win > 0 ? Math.round(wonValue / win) : 0;
  const openValue = leads.filter(l => l.stage !== 'win').reduce((sum, l) => sum + (l.value || 0), 0);
  return <div data-id="element-128">
      <div className="bg-gradient-to-r from-cream-2 to-cream-2/40 border-l-[3px] border-wood rounded-xl p-4 text-[13.5px] mb-5 text-charcoal-soft flex items-start gap-3" data-id="element-129">
        <TrendingDown className="w-5 h-5 text-wood flex-shrink-0 mt-0.5" strokeWidth={2.4} data-id="element-130" />
        <div className="leading-relaxed" data-id="element-131">
          Aquí ves <b className="text-charcoal" data-id="element-132">dónde se te caen las ventas</b>.
          Pierdes en "Cita → Presupuesto" = tardas en dar precio. Pierdes en
          "Presupuesto → Ganada" = falta seguimiento.
        </div>
      </div>

      <KpiGrid kpis={kpis} data-id="element-133" />

      <div className="bg-white border border-line rounded-2xl p-5 md:p-6 shadow-card mb-5" data-id="element-134">
        <div className="flex items-center gap-2 mb-5" data-id="element-135">
          <div className="w-8 h-8 rounded-lg bg-wood/10 text-wood flex items-center justify-center" data-id="element-136">
            <Target className="w-4 h-4" strokeWidth={2.5} data-id="element-137" />
          </div>
          <h3 className="text-[16px] font-poppins font-extrabold tracking-tight" data-id="element-138">
            Embudo de conversión
          </h3>
        </div>
        {steps.map((step, i) => {
        const pctBase = total > 0 ? Math.round(step.v / total * 100) : 0;
        const prev = i > 0 ? steps[i - 1].v : step.v;
        const drop = prev > 0 ? Math.round(step.v / prev * 100) : 0;
        const dropAlert = i > 0 && drop < 50;
        return <div key={i} className="mb-4 last:mb-0" data-id="element-139">
              <div className="flex justify-between items-baseline text-[13px] font-bold mb-1.5" data-id="element-140">
                <span className="text-charcoal" data-id="element-141">{step.l}</span>
                <span className="text-charcoal-soft" data-id="element-142">
                  <span className="font-poppins font-black text-charcoal text-[15px]" data-id="element-143">
                    {step.v}
                  </span>
                  <span className="text-[#8a8175] font-semibold mx-1" data-id="element-144">·</span>
                  <span className="text-wood font-extrabold" data-id="element-145">{pctBase}%</span>
                </span>
              </div>
              <div className="h-3 bg-cream-2 rounded-full overflow-hidden" data-id="element-146">
                <div className="h-full bg-gradient-to-r from-wood to-wood-dark rounded-full transition-all duration-700 ease-out" style={{
              width: `${pctBase}%`
            }} data-id="element-147" />
              </div>
              {i > 0 && <div className={`text-[11.5px] mt-1.5 font-semibold flex items-center gap-1 ${dropAlert ? 'text-red' : 'text-[#8a8175]'}`} data-id="element-148">
                  {dropAlert && <Info className="w-3 h-3" strokeWidth={2.5} data-id="element-149" />}
                  {drop}% pasó desde la etapa anterior
                </div>}
            </div>;
      })}
      </div>

      <div className="bg-white border border-line rounded-2xl p-5 md:p-6 shadow-card" data-id="element-150">
        <div className="flex items-center gap-2 mb-5" data-id="element-151">
          <div className="w-8 h-8 rounded-lg bg-green/10 text-green flex items-center justify-center" data-id="element-152">
            <Wallet className="w-4 h-4" strokeWidth={2.5} data-id="element-153" />
          </div>
          <h3 className="text-[16px] font-poppins font-extrabold tracking-tight" data-id="element-154">
            Dinero
          </h3>
        </div>

        <div className="grid gap-3" data-id="element-155">
          <MoneyRow icon={<Trophy className="w-4 h-4" strokeWidth={2.5} data-id="element-157" />} label="Facturación ganada" value={eur(wonValue)} accent="green" data-id="element-156" />
          <MoneyRow icon={<Coins className="w-4 h-4" strokeWidth={2.5} data-id="element-159" />} label="Ticket medio por obra" value={eur(avgTicket)} accent="wood" data-id="element-158" />
          <MoneyRow icon={<Hourglass className="w-4 h-4" strokeWidth={2.5} data-id="element-161" />} label="En juego (pipeline abierto)" value={eur(openValue)} accent="charcoal" data-id="element-160" />
        </div>
      </div>
    </div>;
}
function MoneyRow({
  icon,
  label,
  value,
  accent
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  accent: 'green' | 'wood' | 'charcoal';
}) {
  const iconBg = accent === 'green' ? 'bg-green/10 text-green' : accent === 'wood' ? 'bg-wood/10 text-wood' : 'bg-charcoal/5 text-charcoal-soft';
  const valueColor = accent === 'green' ? 'text-green' : accent === 'wood' ? 'text-wood' : 'text-charcoal';
  return <div className="flex items-center justify-between py-2.5 px-3 rounded-xl bg-cream/40 border border-line/60" data-id="element-162">
      <div className="flex items-center gap-3" data-id="element-163">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconBg}`} data-id="element-164">
          {icon}
        </div>
        <span className="text-[13px] font-bold text-charcoal" data-id="element-165">{label}</span>
      </div>
      <span className={`font-poppins font-black text-[16px] tracking-tight ${valueColor}`} data-id="element-166">
        {value}
      </span>
    </div>;
}